import { useQuery } from '@tanstack/react-query'
import { Card, Flex, Skeleton, Tooltip, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import { organizationsAuditLogsSignInActivityRetrieveOptions } from 'src/api/main'
import { formatDate } from 'src/utils/dateTime'

import { useStyles } from './styles'

const { Text, Title } = Typography

// The chart shows a fixed 30-day window; AXIS_LABELS below assume this span.
const DAYS = 30
const AXIS_LABELS = ['30d', '25d', '20d', '15d', '10d', '5d', 'Today']

const formatPercent = (pct: number) =>
  `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`

const LegendChip = ({ color }: { color: string }) => (
  <span
    style={{
      width: 8,
      height: 8,
      borderRadius: 2,
      background: color,
      flex: '0 0 auto'
    }}
  />
)

type Props = {
  workspaceId: number
}

export const OverviewSignInChart = ({ workspaceId }: Props) => {
  const { styles } = useStyles()
  const { t } = useTranslation()

  const idValid = Number.isFinite(workspaceId) && workspaceId > 0

  const { data, isLoading } = useQuery({
    ...organizationsAuditLogsSignInActivityRetrieveOptions({
      path: { organization_id: workspaceId },
      query: { days: DAYS, include_comparison: true }
    }),
    enabled: idValid
  })

  const summary = data?.summary
  const series = data?.series ?? []
  const max = Math.max(
    ...series.map((point) => point.sign_ins + point.failures),
    1
  )
  const signInsChange = summary?.sign_ins_change_percent ?? null

  return (
    <Card variant="outlined" className={styles.panelCard}>
      <Flex className={styles.panelHeader}>
        <Flex vertical gap={2} style={{ minWidth: 0 }}>
          <Text className={styles.panelTitle}>
            {t('workspaceOverview.signInActivity.title')}
          </Text>
          <Text className={styles.panelSubtitle}>
            {t('workspaceOverview.signInActivity.subtitle')}
          </Text>
        </Flex>
        <Flex align="center" gap={10} wrap="wrap">
          <Flex align="center" gap={4}>
            <LegendChip color="var(--ant-color-primary)" />
            <Text className={styles.panelSubtitle}>
              {t('workspaceOverview.signInActivity.legendSignIns')}
            </Text>
          </Flex>
          <Flex align="center" gap={4}>
            <LegendChip color="var(--ant-color-error)" />
            <Text className={styles.panelSubtitle}>
              {t('workspaceOverview.signInActivity.legendFailures')}
            </Text>
          </Flex>
        </Flex>
      </Flex>

      {isLoading ? (
        <div style={{ padding: '14px 16px 16px' }}>
          <Skeleton active paragraph={{ rows: 4 }} title={false} />
        </div>
      ) : (
        <div style={{ padding: '14px 16px 16px' }}>
          <Flex gap={36} wrap="wrap" style={{ marginBottom: 12 }}>
            <Flex vertical gap={4}>
              <Text className={styles.chartKpiLabel}>
                {t('workspaceOverview.signInActivity.signIns')}
              </Text>
              <Flex align="baseline" gap={6}>
                <Title level={3} className={styles.chartKpiValue}>
                  {(summary?.sign_ins ?? 0).toLocaleString()}
                </Title>
                {signInsChange !== null ? (
                  <Text
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      color:
                        signInsChange >= 0
                          ? 'var(--ant-color-success)'
                          : 'var(--ant-color-error)'
                    }}
                  >
                    {formatPercent(signInsChange)}
                  </Text>
                ) : null}
              </Flex>
            </Flex>
            <Flex vertical gap={4}>
              <Text className={styles.chartKpiLabel}>
                {t('workspaceOverview.signInActivity.avgPerDay')}
              </Text>
              <Title level={3} className={styles.chartKpiValue}>
                {(summary?.avg_per_day ?? 0).toFixed(1)}
              </Title>
            </Flex>
            <Flex vertical gap={4}>
              <Text className={styles.chartKpiLabel}>
                {t('workspaceOverview.signInActivity.failed')}
              </Text>
              <Flex align="baseline" gap={6}>
                <Title
                  level={3}
                  className={styles.chartKpiValue}
                  style={{ color: 'var(--ant-color-error)' }}
                >
                  {(summary?.failures ?? 0).toLocaleString()}
                </Title>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {(summary?.failure_rate_percent ?? 0).toFixed(1)}%
                </Text>
              </Flex>
            </Flex>
            <Flex vertical gap={4}>
              <Text className={styles.chartKpiLabel}>
                {t('workspaceOverview.signInActivity.uniqueUsers')}
              </Text>
              <Title level={3} className={styles.chartKpiValue}>
                {(summary?.unique_users ?? 0).toLocaleString()}
              </Title>
            </Flex>
          </Flex>

          <Flex
            align="flex-end"
            gap={3}
            style={{ height: 96, width: '100%' }}
            aria-hidden
          >
            {series.map((point) => (
              <Tooltip
                key={point.date}
                mouseEnterDelay={0.1}
                title={
                  <Flex vertical gap={4}>
                    <Text style={{ color: 'inherit', fontWeight: 500 }}>
                      {formatDate(point.date, { timeZone: 'UTC' })}
                    </Text>
                    <Flex align="center" gap={6}>
                      <LegendChip color="var(--ant-color-primary)" />
                      <Text style={{ color: 'inherit', fontSize: 12 }}>
                        {`${t('workspaceOverview.signInActivity.legendSignIns')}: ${point.sign_ins.toLocaleString()}`}
                      </Text>
                    </Flex>
                    {point.failures > 0 && (
                      <Flex align="center" gap={6}>
                        <LegendChip color="var(--ant-color-error)" />
                        <Text style={{ color: 'inherit', fontSize: 12 }}>
                          {`${t('workspaceOverview.signInActivity.legendFailures')}: ${point.failures.toLocaleString()}`}
                        </Text>
                      </Flex>
                    )}
                  </Flex>
                }
              >
                <Flex
                  vertical
                  justify="flex-end"
                  className={styles.chartColumn}
                  style={{ flex: 1, height: '100%', cursor: 'default' }}
                >
                  {(point.sign_ins > 0 || point.failures === 0) && (
                    <div
                      style={{
                        height: `${(point.sign_ins / max) * 100}%`,
                        background:
                          point.sign_ins > 0
                            ? 'var(--ant-color-primary)'
                            : 'var(--ant-color-border-secondary)',
                        opacity:
                          point.sign_ins > 0
                            ? 0.55 + 0.45 * (point.sign_ins / max)
                            : 1,
                        borderRadius: point.failures > 0 ? '2px 2px 0 0' : 2,
                        minHeight: 2
                      }}
                    />
                  )}
                  {point.failures > 0 && (
                    <div
                      style={{
                        height: `${(point.failures / max) * 100}%`,
                        background: 'var(--ant-color-error)',
                        borderRadius: point.sign_ins > 0 ? '0 0 2px 2px' : 2,
                        minHeight: 2
                      }}
                    />
                  )}
                </Flex>
              </Tooltip>
            ))}
          </Flex>

          <Flex justify="space-between" style={{ marginTop: 6 }}>
            {AXIS_LABELS.map((label) => (
              <Text key={label} className={styles.chartAxis}>
                {label}
              </Text>
            ))}
          </Flex>
        </div>
      )}
    </Card>
  )
}
